// Layouts do roteador: AuthGate (aguarda /me), AppShell e guardas de escopo (rede/tenant/estudo — Parte 14.7 regra 7).
import { useTranslation } from 'react-i18next';
import { Outlet, useParams, useRouteError } from 'react-router';

import { useAuth, useMe } from '../lib/auth/authContext';
import { ContextRouteSync } from '../lib/context/ContextRouteSync';
import { canEnterNetworkRoute, canEnterTenantRoute } from '../lib/context/contextResolver';
import { NotFoundPage } from '../pages/NotFound';
import { AppShell } from '../shell/AppShell';
import { EntraAccessPortal } from '../shell/EntraAccessPortal';
import { correlationIdOf, defaultClientCorrelationId } from '../shell/correlation';
import { ErrorFallback } from '../shell/ErrorBoundary';

function AuthStatusScreen({ label }: { readonly label: string }) {
  return (
    <div
      className="flex min-h-svh items-center justify-center bg-background text-foreground"
      role="status"
      aria-live="polite"
      data-testid="auth-status"
    >
      <p className="text-muted-foreground">{label}</p>
    </div>
  );
}

/** Só renderiza os filhos com `/me` carregado; se a sessão expirar ou houver erro de login, volta para a tela de login. */
export function AuthGate() {
  const { t } = useTranslation('shell');
  const { status } = useAuth();
  if (status === 'authenticated') {
    return <Outlet />;
  }
  if (status === 'error') {
    return (
      <EntraAccessPortal statusLabel={t('auth.failedTitle')} initialError={t('login.error')} />
    );
  }
  if (status === 'signing-in') {
    return <EntraAccessPortal statusLabel={t('auth.signingIn')} />;
  }
  return <AuthStatusScreen label={t('auth.loading')} />;
}

/** Shell autenticado. */
export function App() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/** `/network/*`: sem `networkAccess.hasAccess` → NotFound local (nenhuma chamada à API). */
export function NetworkScopeLayout() {
  const me = useMe();
  if (!canEnterNetworkRoute(me)) {
    return <NotFoundPage />;
  }
  return (
    <>
      <ContextRouteSync scope="network" />
      <Outlet />
    </>
  );
}

/** `/t/:tenantCode/*`: código fora de `contexts[]` → NotFound local (espelha o 404 do backend). */
export function TenantScopeLayout() {
  const me = useMe();
  const { tenantCode, studyCode } = useParams();
  if (!tenantCode || !canEnterTenantRoute(me, tenantCode)) {
    return <NotFoundPage />;
  }
  return (
    <>
      <ContextRouteSync scope="tenant" tenantCode={tenantCode} studyCode={studyCode} />
      <Outlet />
    </>
  );
}

/** `errorElement` do roteador: erros de loaders/render fora do ErrorBoundary do shell. */
export function RouteErrorPage() {
  const error = useRouteError();
  return (
    <ErrorFallback
      error={error}
      correlationId={correlationIdOf(error, defaultClientCorrelationId)}
      onRetry={() => {
        window.location.reload();
      }}
    />
  );
}
